import { FolderFilled, MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Table, Tag, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { TABLE_SURFACES } from "../../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../../components/Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../hooks/use-column-resize.tsx"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { useTableColumnConfig } from "../../../hooks/use-table-column-config.tsx"
import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { documentTypeTagColor } from "../helpers/document-type-tag-color.ts"
import { folderItemCount } from "../helpers/folder-item-count.ts"
import { readFileDate } from "../helpers/read-file-date.ts"
import { type FileItem } from "../types/file-item.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"

const { Text } = Typography

interface FolderRow {
  key: string
  folder: FolderListItem
}

type ListRow = FileItem | FolderRow

export interface FileOnlyColumn {
  title: React.ReactNode
  key: string
  renderFile: (file: FileItem) => React.ReactNode
}

const isFolderRow = (row: ListRow): row is FolderRow => "folder" in row

interface FilesListViewProps {
  items: FileItem[]
  actions: (file: FileItem) => MenuItemType[]
  /** Listed above the files, in the order given. */
  folders?: FolderListItem[]
  folderActions?: (folder: FolderListItem) => MenuItemType[]
  onOpenFolder?: (folder: FolderListItem) => void
  /** Omit both together for a view with no bulk actions behind it (Trash) —
   *  a selection checkbox nothing acts on would be exactly the kind of fake
   *  control the Files feature explicitly avoids. */
  selectedKeys?: Set<string>
  onSelectionChange?: (keys: string[]) => void
  onPreview: (file: FileItem) => void
  isFavorite?: (file: FileItem) => boolean
  onToggleFavorite?: (file: FileItem) => void
  /** Trash: swaps the Modified column for Deleted by/Deleted — same table,
   *  same Actions-column mechanism (the caller's own `actions(file)`
   *  supplies Restore/Delete permanently instead of the normal menu), not a
   *  second File Table implementation. */
  trashMode?: boolean
  /** Global Files: extra columns (Document Type/Related To/Customer/
   *  Uploaded By) spliced in after Size — every per-project surface omits
   *  this and keeps the compact default column set. */
  extraColumns?: FileOnlyColumn[]
  /** Distinct persisted-column-config key for a caller whose column set
   *  genuinely differs from the per-project default — omitted everywhere
   *  else, which keeps `trashMode` choosing between "default"/"trash". */
  columnConfigMode?: string
}

const EMPTY_CELL = "—"

const cell =
  (
    ofFile: (file: FileItem) => React.ReactNode,
    ofFolder: (folder: FolderListItem) => React.ReactNode
  ) =>
  (_: unknown, row: ListRow) =>
    isFolderRow(row) ? ofFolder(row.folder) : ofFile(row)

export function FilesListView({
  items,
  actions,
  folders = [],
  folderActions,
  onOpenFolder,
  selectedKeys,
  onSelectionChange,
  onPreview,
  isFavorite,
  onToggleFavorite,
  trashMode = false,
  extraColumns = [],
  columnConfigMode
}: FilesListViewProps) {
  const { t } = useTranslation()
  const { dateFormat, timezone } = useDateFormatter()

  const customerTag = (createdByName: string | null | undefined) => (
    <Tag color="blue" className="!m-0 shrink-0">
      {createdByName
        ? t("files.filters.sharedByCustomerNamed", { name: createdByName })
        : t("files.filters.sharedByCustomer")}
    </Tag>
  )

  const rowMenu = (menuItems: MenuItemType[], name: string) =>
    menuItems.length ? (
      <Dropdown trigger={["click"]} menu={{ items: menuItems }}>
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined />}
          aria-label={t("files.actions.menu", { name })}
        />
      </Dropdown>
    ) : null

  // All Files is the master inventory of every uploaded file, most of which
  // are never classified — a Document Type/Status/Version column that reads
  // "Not assigned" on most rows reads as broken, not as a feature. The one
  // thing worth surfacing here is *whether* a file has been turned into a
  // Document, as a compact tag beside its name; the full picture (Status,
  // Version, Related To) lives in File Details, and assigning/changing the
  // type is a menu action (`files.actions.assignDocumentType`), not an
  // always-visible picker.
  const trailingColumns: NonNullable<TableProps<ListRow>["columns"]> = trashMode
    ? [
        {
          title: t("files.trash.columns.deletedBy"),
          key: "deletedBy",
          render: cell(
            (file) => file.deletedByName || EMPTY_CELL,
            () => EMPTY_CELL
          )
        },
        {
          title: t("files.trash.columns.deleted"),
          key: "deleted",
          render: cell(
            (file) => getFormatDate(file.deletedAt) ?? EMPTY_CELL,
            () => EMPTY_CELL
          )
        }
      ]
    : [
        {
          title: t("files.columns.modified"),
          key: "modified",
          render: cell(
            (file) => getFormatDate(file.updated) ?? EMPTY_CELL,
            (folder) =>
              getFormatDate(readFileDate(folder.updated, { dateFormat, timezone })) ??
              EMPTY_CELL
          )
        }
      ]

  const fullColumns: TableProps<ListRow>["columns"] = [
    {
      title: t("files.columns.name"),
      key: "name",
      align: "left",
      render: cell(
        (file) => (
          <Flex align="center" gap="small">
            {onToggleFavorite && (
              <Button
                type="text"
                size="small"
                aria-label={t("files.actions.favorite", { name: file.name })}
                icon={
                  isFavorite?.(file) ? (
                    <StarFilled className="text-warning" />
                  ) : (
                    <StarOutlined />
                  )
                }
                onClick={() => onToggleFavorite(file)}
              />
            )}
            <span className="[&_svg]:text-base">{getFileIcon(file.mimeType)}</span>
            <a onClick={() => onPreview(file)}>
              <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
            </a>
            {file.documentType && (
              <Tag color={documentTypeTagColor(file.documentType)} className="!m-0 shrink-0">
                {file.documentTypeLabel}
              </Tag>
            )}
            {file.customerUploaded && customerTag(file.createdByName)}
          </Flex>
        ),
        (folder) => (
          <Flex align="center" gap="small">
            {/* Stands in for the favourite button so the icons line up. */}
            {onToggleFavorite && <span className="w-6 shrink-0" />}
            <FolderFilled className="text-base text-warning" />
            <a onClick={() => onOpenFolder?.(folder)}>
              <Text ellipsis={{ tooltip: folder.name }}>{folder.name}</Text>
            </a>
            {folder.created_by_is_customer && customerTag(folder.created_by_name)}
          </Flex>
        )
      )
    },
    {
      title: t("files.columns.type"),
      key: "type",
      render: cell(
        (file) =>
          file.fileTypeLabel ??
          (file.source === "album-attachment"
            ? t("files.columns.typeFile")
            : t("files.columns.typeDocument")),
        () => t("files.columns.typeFolder")
      )
    },
    {
      title: t("files.columns.size"),
      key: "size",
      render: cell(
        (file) => file.formattedFileSize || EMPTY_CELL,
        (folder) => t("files.folder.itemCount", { count: folderItemCount(folder) })
      )
    },
    ...extraColumns.map(({ title, key, renderFile }) => ({
      title,
      key,
      render: cell(renderFile, () => EMPTY_CELL)
    })),
    ...trailingColumns,
    {
      title: t("files.columns.actions"),
      key: "actions",
      width: 80,
      render: cell(
        (file) => rowMenu(actions(file), file.name),
        (folder) => rowMenu(folderActions?.(folder) ?? [], folder.name)
      )
    }
  ]

  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.files,
    columnConfigMode ?? (trashMode ? "trash" : "default"),
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<ListRow>({
    fullConfig,
    setColumns
  })

  const hasFileRows = items.length > 0
  const rows: ListRow[] = [
    ...folders.map((folder) => ({ key: `folder:${folder.id}`, folder })),
    ...items
  ]

  return (
    <div ref={tableWrapRef}>
      <Table<ListRow>
        rowKey="key"
        size="small"
        columns={decorateColumns(columns)}
        components={{ header: { cell: ResizableHeaderCell } }}
        dataSource={rows}
        pagination={false}
        rowSelection={
          selectedKeys && onSelectionChange && hasFileRows
            ? {
                selectedRowKeys: [...selectedKeys],
                // Select all and a shift-click range reach `onChange`, never `onSelect`.
                // Not preserved: a key missing from this table is a file off screen.
                onChange: (keys) => onSelectionChange(keys.map(String)),
                // Select all and shift-range selection skip disabled rows.
                getCheckboxProps: (row) => ({ disabled: isFolderRow(row) }),
                renderCell: (_checked, row, _index, node) =>
                  isFolderRow(row) ? null : node
              }
            : undefined
        }
      />
    </div>
  )
}
